import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Target,
  ArrowUpRight,
  Check,
  Sparkles,
  TrendingUp,
  Users,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Target, title: 'Behavior triggers', text: 'Start chats based on page, time, cart, scroll, or return visit.' },
  { icon: Users, title: 'Live visitor view', text: 'See who is on your site and what they are doing right now.' },
  { icon: Sparkles, title: 'Automated outreach', text: 'Send WhatsApp, email, or chat messages when rules fire.' },
  { icon: BarChart3, title: 'Conversion tracking', text: 'Measure how many proactive messages lead to sales or saves.' },
];

const benefits = [
  { icon: TrendingUp, title: '+35% more conversions', description: 'Engage visitors at the right moment to boost sales.' },
  { icon: Target, title: '-25% churn reduction', description: 'Catch frustration before customers leave.' },
  { icon: Users, title: '+30% repeat visits', description: 'Re-engage customers who have not returned.' },
  { icon: Zap, title: 'Real-time engagement', description: 'See and respond to visitor behavior instantly.' },
];

const useCases = [
  { title: 'Cart abandonment', text: 'Offer help or a discount before a shopper leaves.' },
  { title: 'Trial re-engagement', text: 'Reach users who have not logged in for days.' },
  { title: 'Pricing page prompts', text: 'Answer questions while prospects compare options.' },
  { title: 'Help page assistance', text: 'Start a chat when visitors linger on support pages.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What triggers can I use?',
    answer: 'You can trigger messages based on page, time on site, scroll depth, cart value, returning visit, and custom events.',
  },
  {
    question: 'Will proactive chat annoy visitors?',
    answer: 'No. You control frequency, timing, and targeting so messages only appear for the right people at the right moment.',
  },
  {
    question: 'Can I measure conversions?',
    answer: 'Yes. ZuriHub tracks how many proactive chats lead to sales, sign-ups, or ticket deflection.',
  },
  {
    question: 'Which channels work with proactive messaging?',
    answer: 'Proactive messages work across live chat, WhatsApp, email, and other connected channels.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ProactiveServicePage() {
  return (
    <>
      <SEO
        title="Proactive Service - Engage Customers Before They Ask | ZuriHub"
        description="Reach customers before they ask. ZuriHub Proactive Service uses live visitor data, triggers, and campaigns to start conversations and prevent churn."
        path="/proactive-service"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Target className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Start conversations at <span className="gradient-text">exactly the right moment</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Turn website visits, cart actions, and customer signals into timely messages. Help, upsell, and retain before a ticket is opened.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Target className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Active%20Pro.webp"
                  alt="ZuriHub Proactive Service showing live visitor tracking and behavioral triggers"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful proactive features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to engage visitors at the right time.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose proactive service</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform customer engagement.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Install the widget, create triggers based on visitor behavior, and start conversations that convert.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Zap,
                    title: 'Install the widget',
                    description: 'Add ZuriHub to your site to start capturing visitor events.',
                  },
                  {
                    icon: Target,
                    title: 'Create a trigger',
                    description: 'Choose the behavior that starts the conversation.',
                  },
                  {
                    icon: Sparkles,
                    title: 'Set conditions',
                    description: 'Trigger on time on site, scroll depth, cart value, or return visit.',
                  },
                  {
                    icon: BarChart3,
                    title: 'Engage and convert',
                    description: 'Offer help, share content, or start a conversation that leads to a sale.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Proactive service features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Live visitor tracking</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Behavior-based triggers</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Automated outreach</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Conversion tracking</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Multi-channel messaging</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Churn prevention</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start proactive conversations today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Set your first trigger and convert more visitors into customers.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about proactive service</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
